import { GlobeIcon } from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import {
  ImageKind,
  TutorBookingPageResponse,
  WebPageService,
} from "client/openapi";
import { useAutosave } from "components/Forms/useAutosave";
import IconBubble, { BubbleTone } from "components/IconBubble";
import { COMPACT_TEXTAREA } from "components/Inputs/compact";
import { Switch } from "components/Switch";
import {
  LABEL,
  SECTION,
  SECTION_TEXT,
  SECTION_TITLE,
} from "components/ProfileDropdown/settingsStyles";
import { widgetCode } from "util/embed";
import CopyLinkRow from "./CopyLinkRow";
import ImageUpload from "./ImageUpload";
import { PersonPhoto } from "./PublicShell";

/** What the card autosaves. The photo saves through upload and Remove only. */
type Editable = {
  bio: string;
  hidden_from_booking_page?: boolean;
};

/**
 * What families see on a tutor's booking page: photo, bio and the link.
 * Admins also choose whether the tutor is on the booking pages at all.
 * Saves on its own. Used on the tutor page and in the tutor's own Settings.
 */
export default function BookingProfileCard({
  tutorId,
  firstName,
  lastName,
  canHide,
}: {
  tutorId: number;
  firstName: string;
  lastName: string;
  canHide: boolean;
}) {
  const [page, setPage] = useState<TutorBookingPageResponse>();
  const [value, setValue] = useState<Editable>();
  const [photo, setPhoto] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const change = (edit: Partial<Editable>) =>
    setValue((current) => current && { ...current, ...edit });

  const { pending, error, markSaved } = useAutosave(
    value,
    (next: Editable) =>
      WebPageService.updateTutorWebPageByTutorId({
        tutorId,
        requestBody: next,
      }),
    600
  );

  useEffect(() => {
    WebPageService.getTutorWebPageSettings({ tutorId })
      .then((loaded) => {
        const next: Editable = { bio: loaded.bio ?? "" };
        if (canHide)
          next.hidden_from_booking_page = loaded.hidden_from_booking_page;
        markSaved(next);
        setPage(loaded);
        setPhoto(loaded.photo_url ?? null);
        setValue(next);
      })
      .catch(() => setLoadFailed(true));
  }, [tutorId, canHide, markSaved]);

  if (loadFailed)
    return (
      <p className="text-sm text-red-600">
        The booking page could not be loaded.
      </p>
    );
  if (!page || !value) return null;

  return (
    <section className={`${SECTION} max-w-4xl`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <IconBubble tone={BubbleTone.SKY}>
            <GlobeIcon />
          </IconBubble>
          <div>
            <h3 className={SECTION_TITLE}>Booking page</h3>
            <p className={SECTION_TEXT}>
              What families see on {firstName}&apos;s page.
            </p>
          </div>
        </div>
        <p
          className={`text-xs ${error ? "text-red-600" : "text-gray-500"}`}
          aria-live="polite"
        >
          {error || (pending ? "Saving…" : "Saved")}
        </p>
      </div>

      {canHide && (
        <div className="mt-4 max-w-sm">
          <Switch
            name={`tutor-${tutorId}-listed`}
            checked={!value.hidden_from_booking_page}
            onCheckedChange={(on) => change({ hidden_from_booking_page: !on })}
          >
            <label
              htmlFor={`tutor-${tutorId}-listed`}
              className="cursor-pointer text-sm"
            >
              Show {firstName} on the booking pages
            </label>
          </Switch>
        </div>
      )}

      <div className="mt-5 flex flex-col gap-6 sm:flex-row">
        <ImageUpload
          orgId={page.org_web_page.org_id}
          kind={ImageKind.TUTOR_PHOTO}
          tutorId={tutorId}
          hasImage={!!photo}
          noun="photo"
          className="flex shrink-0 flex-col items-center gap-3 sm:w-[168px]"
          centered
          onUploaded={setPhoto}
          onRemove={() =>
            WebPageService.updateTutorWebPageByTutorId({
              tutorId,
              requestBody: { photo_url: null },
            }).then(() => setPhoto(null))
          }
        >
          <PersonPhoto
            url={photo}
            name={`${firstName} ${lastName}`}
            size={168}
            accent="#16A34A"
            square
          />
        </ImageUpload>
        <div className="min-w-0 flex-1">
          <label htmlFor={`tutor-${tutorId}-bio`} className={LABEL}>
            Bio
          </label>
          <textarea
            id={`tutor-${tutorId}-bio`}
            rows={5}
            value={value.bio}
            onChange={(e) => change({ bio: e.target.value })}
            className={`${COMPACT_TEXTAREA} mt-1`}
          />
          <p className={`${LABEL} mt-4`}>Page link</p>
          <CopyLinkRow
            url={page.page_url}
            embed={() =>
              widgetCode(`/tutors/${tutorId}`, `Book ${firstName} ${lastName}`)
            }
          />
          <p className="mt-2 text-xs text-gray-400">
            Times come from the Availability tab. Subjects come from Tutor Info.
          </p>
        </div>
      </div>
    </section>
  );
}
